import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import PublicPageShell, { SectionHeading } from '@/components/PublicPageShell';
import { Star, Mail } from 'lucide-react';

const ROWS = [
  { user: 'Player', price: 'FREE', what: 'Verified profile, PDS tracking, XV Assist AI, opportunity applications, fundraising' },
  { user: 'School', price: 'R19 / player / month', what: 'Full squad & development infrastructure, match management, parent portal, analytics' },
  { user: 'Club', price: 'R39 / player / month', what: 'Full club operations, video analysis, load management, tactical tools, fundraising' },
  { user: 'Academy', price: 'R49 / player / month', what: 'Everything in Club plus squad-level pipeline dashboards and selection dashboards' },
  { user: 'Provincial Union', price: 'R79 / player / month', what: 'Province-wide visibility, representative pathway management, union talent intelligence, full analytics' },
  { user: 'Scout Lite', price: 'R350 / month', what: 'Province & position filtering, limited profile access, basic scouting visibility' },
  { user: 'Scout Pro', price: 'R850 – R1,250 / month', what: 'Full national access, advanced filtering, direct messaging, exports & analytics' },
  { user: 'Agency / Union / Academy', price: 'R2,500 – R10,000 / month', what: 'Multi-user accounts, recruitment dashboards, talent tracking, priority support' },
];

export default function Pricing() {
  const navigate = useNavigate();
  return (
    <PublicPageShell
      eyebrow="PRICING"
      title="Accessible for every level of the game"
      intro="From free player profiles to full provincial union infrastructure — FutureXV is built to scale with your needs."
      dataTestId="pricing-page"
    >
      <div className="bg-[#0a0a0a] border border-zinc-800 rounded-2xl overflow-hidden" data-testid="pricing-table-card">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="pricing-table">
            <thead className="bg-[#050505] border-b border-zinc-800">
              <tr>
                <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400">User</th>
                <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400">Price</th>
                <th className="text-left px-6 py-4 text-xs font-bold uppercase tracking-wider text-zinc-400">What you get</th>
              </tr>
            </thead>
            <tbody>
              {ROWS.map((r, i) => (
                <tr key={r.user} className={`${i % 2 === 0 ? 'bg-transparent' : 'bg-white/[0.015]'} border-b border-zinc-900 last:border-0`} data-testid={`pricing-row-${r.user.toLowerCase().replace(/[\s/]+/g,'-')}`}>
                  <td className="px-6 py-5 text-white font-bold">{r.user}</td>
                  <td className="px-6 py-5"><span className="text-[#A3FF00] font-bold">{r.price}</span></td>
                  <td className="px-6 py-5 text-zinc-400 text-sm leading-relaxed">{r.what}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mt-8 bg-gradient-to-br from-[#0a0a0a] to-[#0a1f15] border border-[#A3FF00]/30 rounded-2xl p-7" data-testid="voucher-callout">
        <div className="flex items-start gap-4">
          <Star className="w-7 h-7 text-[#A3FF00] shrink-0 mt-0.5" />
          <div>
            <p className="text-[#A3FF00] text-xs font-bold tracking-[0.25em] mb-2">COMMUNITY ACCESS VOUCHER PROGRAMME</p>
            <p className="text-zinc-200 leading-relaxed">
              Qualifying EMIS-verified public schools receive full platform access at <strong className="text-white">no cost</strong>.
              Voucher requests: <a href="mailto:info@futurexv.co.za" className="text-[#A3FF00]">info@futurexv.co.za</a>
            </p>
          </div>
        </div>
      </div>

      <SectionHeading>How billing works</SectionHeading>
      <div className="space-y-4 text-zinc-300 leading-relaxed">
        <p>Players are <strong className="text-white">always free</strong> — no tier limits, no upsells, no exceptions.</p>
        <p>
          Institutions are billed monthly per active player. New institutions get a <strong className="text-white">5-day free trial</strong>
          on activation. All payments are processed securely through PayFast in ZAR.
        </p>
        <p>
          Scout tiers are billed monthly with the ability to upgrade or downgrade at any time. Agency / Union / Academy
          tiers are bespoke and include multi-user dashboards plus priority support.
        </p>
      </div>

      <div className="mt-12 flex flex-wrap gap-3">
        <Button onClick={() => navigate('/auth')} className="bg-[#A3FF00] hover:bg-[#88dd00] text-black font-bold px-8 py-6" data-testid="pricing-get-started">
          Get Started
        </Button>
        <a
          href="mailto:info@futurexv.co.za"
          className="inline-flex items-center gap-2 border border-zinc-700 text-zinc-200 hover:bg-zinc-900 px-8 py-3.5 rounded-md transition-colors"
          data-testid="billing-email-link"
        >
          <Mail className="w-4 h-4" />info@futurexv.co.za
        </a>
      </div>
    </PublicPageShell>
  );
}
